import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Trophy, Sparkles, Loader2, Instagram, RefreshCw, AlertCircle, PartyPopper, ShieldCheck, Ticket, Smartphone } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 8 segments alternating: PERDU / GAGNÉ
const SEGMENTS = [
  { label: "PERDU", color: "#FDF0DF", text: "#14230A" },
  { label: "GAGNÉ", color: "#FF4D00", text: "#FDF0DF" },
  { label: "PERDU", color: "#14230A", text: "#FDF0DF" },
  { label: "GAGNÉ", color: "#FF4D00", text: "#FDF0DF" },
  { label: "PERDU", color: "#FDF0DF", text: "#14230A" },
  { label: "GAGNÉ", color: "#FF4D00", text: "#FDF0DF" },
  { label: "PERDU", color: "#14230A", text: "#FDF0DF" },
  { label: "GAGNÉ", color: "#FF4D00", text: "#FDF0DF" },
];
const WIN_INDICES = SEGMENTS.map((s, i) => (s.label === "GAGNÉ" ? i : -1)).filter((i) => i !== -1);
const LOSE_INDICES = SEGMENTS.map((s, i) => (s.label === "PERDU" ? i : -1)).filter((i) => i !== -1);

function WheelSVG({ rotation, spinning }) {
  const cx = 200;
  const cy = 200;
  const r = 180;
  const step = 360 / SEGMENTS.length;

  return (
    <div className="relative w-full max-w-[420px] aspect-square mx-auto">
      <div className="absolute top-[-6px] left-1/2 -translate-x-1/2 z-20">
        <svg width="44" height="48" viewBox="0 0 44 48">
          <polygon points="22,44 4,4 40,4" fill="#FF4D00" stroke="#14230A" strokeWidth="3" strokeLinejoin="round" />
          <circle cx="22" cy="10" r="3" fill="#FDF0DF" />
        </svg>
      </div>
      <svg viewBox="0 0 400 400" className="w-full h-full drop-shadow-[6px_6px_0_rgba(20,35,10,1)]">
        <g
          style={{
            transform: `rotate(${rotation}deg)`,
            transformOrigin: "200px 200px",
            transition: spinning ? "transform 4.5s cubic-bezier(0.18, 0.85, 0.28, 1)" : "none",
          }}
        >
          <circle cx={cx} cy={cy} r={r + 6} fill="#14230A" />
          {SEGMENTS.map((seg, i) => {
            const startAngle = i * step - 90;
            const endAngle = (i + 1) * step - 90;
            const sRad = (startAngle * Math.PI) / 180;
            const eRad = (endAngle * Math.PI) / 180;
            const x1 = cx + r * Math.cos(sRad);
            const y1 = cy + r * Math.sin(sRad);
            const x2 = cx + r * Math.cos(eRad);
            const y2 = cy + r * Math.sin(eRad);
            const labelAngle = startAngle + step / 2;
            const lRad = (labelAngle * Math.PI) / 180;
            const lx = cx + r * 0.62 * Math.cos(lRad);
            const ly = cy + r * 0.62 * Math.sin(lRad);
            return (
              <g key={i}>
                <path
                  d={`M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 0 1 ${x2} ${y2} Z`}
                  fill={seg.color}
                  stroke="#14230A"
                  strokeWidth="3"
                />
                <text
                  x={lx}
                  y={ly}
                  textAnchor="middle"
                  dominantBaseline="central"
                  transform={`rotate(${labelAngle + 90} ${lx} ${ly})`}
                  fill={seg.text}
                  fontFamily="Big Shoulders Display, Barlow Condensed, sans-serif"
                  fontWeight="800"
                  fontSize="28"
                  letterSpacing="2"
                >
                  {seg.label}
                </text>
              </g>
            );
          })}
        </g>
        <circle cx={cx} cy={cy} r="32" fill="#14230A" stroke="#FDF0DF" strokeWidth="6" />
        <circle cx={cx} cy={cy} r="10" fill="#FF4D00" />
      </svg>
    </div>
  );
}

function formatCode(raw) {
  const clean = (raw || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8);
  if (clean.length <= 4) return clean;
  return `${clean.slice(0, 4)}-${clean.slice(4)}`;
}

const PRIZE_ICONS = {
  iphone: Smartphone,
  voucher_15: Ticket,
  voucher_10: Ticket,
  voucher_5: Ticket,
};

export default function Wheel() {
  const [params] = useSearchParams();
  const [code, setCode] = useState(formatCode(params.get("c") || params.get("n") || ""));
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [orderRef, setOrderRef] = useState(params.get("ref") || "");
  const [spinning, setSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [result, setResult] = useState(null);
  const [config, setConfig] = useState({ instagram_url: "https://www.instagram.com/tresorsdeguyane/", prizes: {} });
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/wheel/config`).then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const cleanCode = useMemo(() => code.replace(/-/g, ""), [code]);

  const handleSpin = async (e) => {
    e?.preventDefault();
    setError("");
    setResult(null);

    if (!name.trim()) {
      setError("Ton prénom est obligatoire.");
      return;
    }
    if (!email.trim() || !email.includes("@")) {
      setError("Un email valide est obligatoire.");
      return;
    }
    if (cleanCode.length !== 8 || !/^[A-Z0-9]+$/.test(cleanCode)) {
      setError("Le code fait 8 caractères (lettres + chiffres), regarde sous ta cup.");
      return;
    }

    setSpinning(true);
    try {
      const { data } = await axios.post(`${API}/wheel/spin`, {
        code: cleanCode,
        name: name.trim(),
        email: email.trim(),
        order_ref: orderRef.trim() || undefined,
      });

      const pool = data.won ? WIN_INDICES : LOSE_INDICES;
      const targetIdx = pool[Math.floor(Math.random() * pool.length)];
      const step = 360 / SEGMENTS.length;
      const segCenter = targetIdx * step + step / 2;
      const targetRotation = 360 * 7 - segCenter;
      const finalRotation = rotation + (targetRotation - (rotation % 360));
      setRotation(finalRotation);

      setTimeout(() => {
        setResult(data);
        setSpinning(false);
        if (data.won) {
          if (data.prize_type === "iphone") toast.success("🏆 GOLDEN TICKET — iPhone 14 !");
          else toast.success(`🎉 ${data.prize_label}`);
        }
      }, 4600);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erreur lors du tirage";
      setError(msg);
      setSpinning(false);
    }
  };

  const reset = () => {
    setResult(null);
    setError("");
    setCode("");
    setOrderRef("");
  };

  return (
    <div className="min-h-screen bg-[#FDF0DF] flex flex-col">
      <Header />

      <main className="flex-1 max-w-[1280px] mx-auto px-5 md:px-10 py-10 md:py-16 w-full">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          {/* LEFT */}
          <div className="lg:col-span-5">
            <div className="font-cond uppercase tracking-[0.3em] text-[11px] text-[#FF4D00] flex items-center gap-2">
              <Trophy className="w-3.5 h-3.5" /> Jeu de la cup · URBX 2026
            </div>
            <h1 className="font-display text-5xl md:text-7xl lg:text-8xl mt-2 leading-[0.9]">
              Tu vas
              <br />
              <span className="text-[#FF4D00]">peut-être</span>
              <br />
              gagner.
            </h1>
            <p className="mt-5 text-[#14230A]/80 leading-relaxed max-w-md">
              Sous chaque ecocup, un <b>code unique de 8 caractères</b>. Entre-le, fais tourner la roue,
              et découvre instantanément si tu remportes un des 156 lots.
            </p>

            {!result ? (
              <form onSubmit={handleSpin} className="mt-8 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="font-cond uppercase text-[11px] tracking-[0.25em]">
                      Prénom *
                    </label>
                    <input
                      data-testid="wheel-name-input"
                      type="text"
                      required
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      disabled={spinning}
                      className="mt-1.5 w-full h-11 px-3 bg-transparent border-2 border-[#14230A] focus:border-[#FF4D00] outline-none font-cond text-base"
                    />
                  </div>
                  <div>
                    <label className="font-cond uppercase text-[11px] tracking-[0.25em]">
                      Email *
                    </label>
                    <input
                      data-testid="wheel-email-input"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={spinning}
                      className="mt-1.5 w-full h-11 px-3 bg-transparent border-2 border-[#14230A] focus:border-[#FF4D00] outline-none font-cond text-base"
                    />
                  </div>
                </div>

                <div>
                  <label className="font-cond uppercase text-[11px] tracking-[0.25em]">
                    Code sous ta cup *
                  </label>
                  <input
                    data-testid="wheel-code-input"
                    type="text"
                    autoComplete="off"
                    placeholder="ABCD-EFGH"
                    value={code}
                    onChange={(e) => setCode(formatCode(e.target.value))}
                    disabled={spinning}
                    className="mt-1.5 w-full h-14 px-4 bg-transparent border-2 border-[#14230A] focus:border-[#FF4D00] outline-none font-display text-3xl tracking-[0.2em] uppercase"
                  />
                  <div className="mt-1 text-[11px] opacity-60">
                    8 caractères, lettres et chiffres uniquement.
                  </div>
                </div>

                <details className="border-2 border-[#14230A]/30">
                  <summary className="px-3 py-2 cursor-pointer font-cond uppercase text-[11px] tracking-[0.2em] hover:text-[#FF4D00]">
                    + Ajouter ma référence de commande (optionnel)
                  </summary>
                  <div className="p-3 pt-1">
                    <input
                      data-testid="wheel-order-ref-input"
                      type="text"
                      placeholder="URBX-XXXXXX"
                      value={orderRef}
                      onChange={(e) => setOrderRef(e.target.value.toUpperCase())}
                      disabled={spinning}
                      className="w-full h-11 px-3 bg-transparent border-2 border-[#14230A] focus:border-[#FF4D00] outline-none font-cond text-base"
                    />
                    <div className="mt-1.5 text-[11px] opacity-70 leading-snug">
                      Bonus si tu gagnes un bon : on vérifie automatiquement que ton achat couvre le
                      minimum requis. Sinon, tu pourras le présenter sur place.
                    </div>
                  </div>
                </details>

                {error && (
                  <div className="border-2 border-[#C53A1B] text-[#C53A1B] p-3 inline-flex items-center gap-2 w-full">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <span className="text-sm">{error}</span>
                  </div>
                )}

                <button
                  data-testid="wheel-spin-btn"
                  type="submit"
                  disabled={spinning}
                  className="w-full h-14 bg-[#FF4D00] text-[#FDF0DF] font-cond uppercase tracking-[0.25em] text-base font-bold inline-flex items-center justify-center gap-2 btn-brut hover:bg-[#D43E00] disabled:opacity-60"
                >
                  {spinning ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" /> Tirage en cours…
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" /> Faire tourner la roue
                    </>
                  )}
                </button>

                <p className="text-[11px] opacity-60 leading-relaxed">
                  156 lots à gagner. Chaque code ne se joue qu’une seule fois. Si tu perds, retente
                  ta chance sur Instagram (150 € à gagner sur tresorsdeguyane.fr).
                </p>
              </form>
            ) : (
              <ResultBlock result={result} onReset={reset} instagramUrl={config.instagram_url} />
            )}
          </div>

          {/* WHEEL */}
          <div className="lg:col-span-7 flex flex-col items-center">
            <div className="relative w-full max-w-[460px] flex flex-col items-center">
              <WheelSVG rotation={rotation} spinning={spinning} />
              {result && (
                <AnimatePresence>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.2 }}
                    className="mt-8 text-center"
                  >
                    {result.won ? (
                      <div className="font-display text-6xl md:text-7xl text-[#FF4D00] flex items-center gap-3 justify-center">
                        <PartyPopper className="w-12 h-12" /> GAGNÉ !
                      </div>
                    ) : (
                      <div className="font-display text-5xl md:text-6xl text-[#14230A] opacity-70">
                        DOMMAGE
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>
              )}
            </div>
          </div>
        </div>

        {/* Prize list */}
        <section className="mt-20 border-t-2 border-[#14230A] pt-12">
          <div className="font-cond uppercase tracking-[0.3em] text-[11px] text-[#FF4D00]">
            156 lots à gagner
          </div>
          <h3 className="font-display text-3xl md:text-5xl mt-1">Ce qui se cache sous les cups</h3>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <PrizeTile icon={Smartphone} count={1} title="iPhone 14" sub="reconditionné" highlight />
            <PrizeTile icon={Ticket} count={5} title="Bon de 15 €" sub="achat min. 150 €" />
            <PrizeTile icon={Ticket} count={50} title="Bon de 10 €" sub="achat min. 100 €" />
            <PrizeTile icon={Ticket} count={100} title="Bon de 5 €" sub="achat min. 50 €" />
          </div>
        </section>

        {/* Rules + last chance */}
        <section className="mt-16 grid md:grid-cols-2 gap-8">
          <div>
            <div className="font-cond uppercase tracking-[0.3em] text-[11px] text-[#FF4D00]">
              Règles du jeu
            </div>
            <h3 className="font-display text-3xl md:text-4xl mt-1 leading-tight">Comment ça marche ?</h3>
            <ol className="mt-5 space-y-3 text-sm">
              {[
                "Précommande tes boissons et récupère ton ecocup au stand le 20 juin.",
                "Sous ta cup, un code de 8 caractères (lettres + chiffres) est imprimé.",
                "Reviens sur cette page, entre ton prénom, email et code, fais tourner la roue.",
                "Chaque code ne joue qu’une seule fois — pas de second essai.",
                "Pour les bons d’achat, un minimum d’achat est demandé pour valider le lot.",
              ].map((step, i) => (
                <li key={i} className="flex gap-3">
                  <span className="font-display text-2xl text-[#FF4D00] leading-none w-7">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-[#14230A]/85 pt-0.5">{step}</span>
                </li>
              ))}
            </ol>
          </div>

          <div className="bg-[#14230A] text-[#FDF0DF] p-7 relative overflow-hidden">
            <div className="grain absolute inset-0 pointer-events-none" />
            <div className="relative">
              <div className="font-cond uppercase tracking-[0.3em] text-[11px] text-[#FF4D00] flex items-center gap-2">
                <Instagram className="w-3.5 h-3.5" /> Seconde chance · Instagram
              </div>
              <h3 className="font-display text-3xl md:text-4xl mt-1 leading-tight">
                Pas gagné ? Va sur le post.
              </h3>
              <p className="mt-3 text-sm opacity-85 leading-relaxed">
                Sur Instagram, on tire au sort <b>150 € à dépenser sur tresorsdeguyane.fr</b>{" "}
                à la fin du festival. Like, commente 2 amis et abonne-toi pour participer.
              </p>
              <a
                href={config.instagram_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="wheel-instagram-link"
                className="mt-6 inline-flex items-center gap-2 px-5 h-12 bg-[#FF4D00] text-[#FDF0DF] font-cond uppercase tracking-[0.2em] text-xs font-bold hover:bg-[#FDF0DF] hover:text-[#14230A]"
              >
                <Instagram className="w-4 h-4" /> Voir le post @tresorsdeguyane
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

function PrizeTile({ icon: Icon, count, title, sub, highlight, image }) {
  return (
    <div
      className={`relative border-2 p-5 flex flex-col items-start overflow-hidden ${
        highlight ? "border-[#FF4D00] bg-[#FF4D00] text-[#FDF0DF]" : "border-[#14230A]"
      }`}
    >
      {image && (
        <img
          src={image}
          alt=""
          aria-hidden="true"
          loading="lazy"
          className="absolute right-[-20px] bottom-[-10px] w-32 h-32 object-contain opacity-30 pointer-events-none"
        />
      )}
      <Icon className={`relative w-7 h-7 ${highlight ? "" : "text-[#FF4D00]"}`} />
      <div className={`relative font-cond uppercase text-[10px] tracking-[0.25em] mt-3 ${highlight ? "opacity-90" : "opacity-60"}`}>
        × {count}
      </div>
      <div className="relative font-display text-3xl mt-1 leading-tight">{title}</div>
      <div className={`relative text-xs mt-1 ${highlight ? "opacity-90" : "opacity-70"}`}>{sub}</div>
    </div>
  );
}

function ResultBlock({ result, onReset, instagramUrl }) {
  const Icon = PRIZE_ICONS[result.prize_type] || Trophy;
  if (result.won && result.prize_type === "iphone") {
    return (
      <div data-testid="wheel-result-iphone" className="mt-8 border-2 border-[#FF4D00] bg-[#FF4D00] text-[#FDF0DF] p-6">
        <div className="font-cond uppercase tracking-[0.3em] text-[11px]">★ Golden Ticket ★</div>
        <div className="font-display text-4xl md:text-5xl mt-2 leading-none flex items-center gap-3">
          <Smartphone className="w-10 h-10" /> iPhone 14
        </div>
        <p className="mt-3 text-sm opacity-95 leading-relaxed">
          INCROYABLE. Code <b>{result.code}</b> = jackpot. Présente-toi au stand Trésors de Guyane
          avec ta cup pour vérification, ou contacte-nous à <b>urbxfestival@tresorsdeguyane.fr</b>.
        </p>
        <div className="mt-5 grid sm:grid-cols-2 gap-2">
          <Link
            to="/"
            data-testid="wheel-back-home"
            className="h-11 px-4 inline-flex items-center justify-center bg-[#14230A] text-[#FDF0DF] font-cond uppercase tracking-[0.2em] text-xs"
          >
            Retour à l’accueil
          </Link>
          <a
            href={instagramUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="h-11 px-4 inline-flex items-center justify-center border-2 border-[#FDF0DF] font-cond uppercase tracking-[0.2em] text-xs"
          >
            Suivre @tresorsdeguyane
          </a>
        </div>
      </div>
    );
  }

  if (result.won) {
    // Voucher
    return (
      <div data-testid="wheel-result-voucher" className="mt-8 border-2 border-[#14230A] p-6 bg-[#FDF0DF]">
        <div className="font-cond uppercase tracking-[0.3em] text-[11px] text-[#FF4D00]">★ Lot remporté ★</div>
        <div className="font-display text-4xl md:text-5xl mt-2 leading-none flex items-center gap-3">
          <Icon className="w-10 h-10 text-[#FF4D00]" /> {result.prize_eur}&nbsp;€
        </div>
        <div className="font-cond uppercase text-[11px] tracking-[0.2em] mt-1 text-[#14230A]/70">
          Bon d’achat sur tresorsdeguyane.fr
        </div>

        {result.proof_validated ? (
          <div className="mt-4 border-2 border-[#14230A] bg-[#14230A] text-[#FDF0DF] p-3 text-sm inline-flex items-start gap-2">
            <ShieldCheck className="w-5 h-5 flex-shrink-0 text-[#FF4D00]" />
            <span>
              Commande <b>{result.code}</b> vérifiée — tu as bien atteint le seuil de{" "}
              {result.min_purchase_eur} €. Présente ta cup et ton ticket au stand pour récupérer ton
              bon d’achat.
            </span>
          </div>
        ) : (
          <div className="mt-4 border-2 border-[#FF4D00] p-3 text-sm">
            <div className="font-cond uppercase tracking-[0.2em] text-[10px] text-[#FF4D00]">
              Conditions du lot
            </div>
            <div className="mt-1 text-[#14230A]">
              Achat minimum requis : <b>{result.min_purchase_eur}&nbsp;€</b> de boissons (hors caution
              ecocup) sur cet événement URBX.
            </div>
            {result.proof_message && (
              <div className="mt-2 text-[12px] text-[#C53A1B]">{result.proof_message}</div>
            )}
            <div className="mt-2 text-[12px] text-[#14230A]/80">
              Présente ta cup <b>+ ton email de confirmation URBX-XXXXXX</b> au stand Trésors de
              Guyane. Le staff vérifie sur place et te remet ton bon.
            </div>
          </div>
        )}

        <div className="mt-5 grid sm:grid-cols-2 gap-2">
          <Link
            to="/"
            data-testid="wheel-back-home"
            className="h-11 px-4 inline-flex items-center justify-center bg-[#14230A] text-[#FDF0DF] font-cond uppercase tracking-[0.2em] text-xs"
          >
            Retour à l’accueil
          </Link>
          <a
            href="https://tresorsdeguyane.fr"
            target="_blank"
            rel="noopener noreferrer"
            className="h-11 px-4 inline-flex items-center justify-center border-2 border-[#14230A] font-cond uppercase tracking-[0.2em] text-xs hover:bg-[#14230A] hover:text-[#FDF0DF]"
          >
            tresorsdeguyane.fr
          </a>
        </div>
      </div>
    );
  }

  // LOSE
  return (
    <div data-testid="wheel-result-lost" className="mt-8 border-2 border-[#14230A] p-6">
      <div className="font-cond uppercase tracking-[0.3em] text-[11px] text-[#14230A]/60">
        {result.already_played ? "Code déjà joué" : "Pas cette fois"}
      </div>
      <div className="font-display text-3xl md:text-4xl mt-2 leading-tight text-[#14230A]">
        {result.already_played ? (
          <>
            Le code <span className="text-[#FF4D00]">{result.code}</span> a déjà été joué.
          </>
        ) : (
          <>Pas de lot ce coup-ci.</>
        )}
      </div>
      <p className="mt-3 text-sm text-[#14230A]/75">
        {result.already_played
          ? "Chaque code ne joue qu’une seule fois. Mais tu peux toujours tenter ta chance sur Instagram."
          : "Tente ta chance sur Instagram pour 150 € à dépenser sur tresorsdeguyane.fr."}
      </p>
      <div className="mt-5 grid sm:grid-cols-2 gap-2">
        <button
          onClick={onReset}
          data-testid="wheel-reset"
          className="h-11 px-4 inline-flex items-center justify-center gap-2 border-2 border-[#14230A] font-cond uppercase tracking-[0.2em] text-xs hover:bg-[#14230A] hover:text-[#FDF0DF]"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Autre code
        </button>
        <a
          href={instagramUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="wheel-go-instagram"
          className="h-11 px-4 inline-flex items-center justify-center gap-2 bg-[#FF4D00] text-[#FDF0DF] font-cond uppercase tracking-[0.2em] text-xs font-bold"
        >
          <Instagram className="w-3.5 h-3.5" /> Tenter Instagram
        </a>
      </div>
    </div>
  );
}
